import { PropsWithChildren, useCallback, useEffect, useState, lazy, Suspense } from 'react'

import { useNavigate, useLocation } from 'react-router-dom'

import {
  Button,
  Icon,
  IconName,
  RightSideNav,
  BottomNav,
  AuthModal,
  PostModal,
  ProfileEditModal,
  CreateTeamModal,
  CreateCommunityModal,
  CreateTournamentModal,
  SetScoreModal,
  Typography,
  Box,
  CreateGameModal,
  SetMatchWinnerModal,
} from 'components'
import SettingsModal from 'components/SettingsModal'
import { formatAddress } from 'utils'

import { useApp } from 'store/app/hooks'
import { useAuth } from 'store/auth/hooks'

import { getProfileDetailRoute, ROUTES } from 'settings/constants'

import DefaultAvatar from 'assets/avatar.webp'
import Logo from 'assets/logo.png'

import {
  sideMenu,
  isExploreRoute,
  isSideMenuItemVisible,
  SideMenuItem,
} from './sidemenu'

import { useUserWallet } from 'hooks/UserWalletContext'
import { usePrivy } from '@privy-io/react-auth'
import { useNotification } from 'store/notification/hooks'
import { useDispatch } from 'react-redux'

import io from 'socket.io-client'
import { updateNotifications } from 'store/notification/slice'
import { updatePostBlockchainMetadata, updateCommentBlockchainMetadata } from 'store/news/slice'
// Lazy load landing pages - these are only needed for unauthenticated users
// This prevents 1MB+ of JS from being bundled into the main chunk
const LandingPage = lazy(() => import('views/LandingPage'))
const DePIN = lazy(() => import('views/DePIN'))
const GameServer = lazy(() => import('views/GameServer'))
import { isCloudSubdomain, isGameServersSubdomain } from 'utils/subdomain'

// Use VITE_API_BASE_URL and strip /api/v1 for socket connections
const webSocketServerLink: string =
  (import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api/v1').replace('/api/v1', '')

export const Layout = ({
                         hasRightNav,
                         fullWidth = false,
                         children,
                       }: PropsWithChildren<{ hasRightNav: boolean; fullWidth?: boolean }>) => {
  const { isLoggedIn, user, signOut, isLoading } = useAuth()
  const { getNotifications, unReadNotifications } = useNotification()

  const { logout, ready: privyReady, authenticated: privyAuthenticated } = usePrivy()
  const { setIsPostModalOpen } = useApp()

  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()

  const { account, disconnect } = useUserWallet()

  // Settings modal state
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)

  // Public routes that don't require authentication
  const publicRoutes = ['/depin', '/token', '/game-server', '/nodes', '/studios', '/privacy', '/terms-of-service']
  // /r/:ref invite landing must reach logged-out visitors (they're the audience),
  // not fall through to the marketing landing page.
  const isPublicRoute =
    publicRoutes.includes(location.pathname) ||
    location.pathname.startsWith('/r/') ||
    // Per-game hosting pages (/game-server/cod2x, ...) are sales pages for logged-out visitors and crawlers.
    location.pathname.startsWith('/game-server/')

  // Allow viewing posts/shouts without authentication (for sharing)
  const isShoutRoute = location.pathname.startsWith('/shout/')
  const isTournamentRoute = location.pathname.startsWith('/tournament/')
  const isStatsRoute = location.pathname.startsWith('/stats/')
  const isBracketShareRoute = location.pathname.startsWith('/brackets/')

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path)
    },
    [navigate],
  )

  // Wait for Privy to finish logging out before clearing Redux, otherwise the
  // App auto-sync sees "Privy authenticated + no Redux user" and refetches /me.
  const handleLogout = async () => {
    try {
      await logout()
    } finally {
      signOut()
      navigate('/')
    }
  }

  // Handle redirect after OAuth login
  useEffect(() => {
    // Check both localStorage and sessionStorage
    const returnUrl = localStorage.getItem('privy_return_url') || sessionStorage.getItem('privy_return_url')
    const loginFromTournament = localStorage.getItem('login_from_tournament') || sessionStorage.getItem('login_from_tournament')
    const hasOAuthParams = location.search.includes('privy_oauth')
    const isOnRoot = location.pathname === '/'

    console.log('🔍 Layout redirect check:', {
      returnUrl,
      loginFromTournament,
      hasOAuthParams,
      isOnRoot,
      hasUser: !!user?._id,
      currentPath: location.pathname,
      fullUrl: window.location.href
    })

    if (returnUrl && user?._id && loginFromTournament) {
      // Only redirect if we're coming back from OAuth (root path or has OAuth params)
      if (isOnRoot || hasOAuthParams) {
        console.log('✅ OAuth complete - Redirecting back to:', returnUrl)
        localStorage.removeItem('privy_return_url')
        localStorage.removeItem('login_from_tournament')
        sessionStorage.removeItem('privy_return_url')
        sessionStorage.removeItem('login_from_tournament')
        navigate(returnUrl, { replace: true })
      }
    }
  }, [user?._id, location.pathname, location.search, navigate])

  // console.log(unReadNotifications, 'unreadNotifications')

  useEffect(() => {
    if (user?._id) getNotifications()
  }, [user?._id])
  useEffect(() => {
    if (user?._id) {
      const socket = io(webSocketServerLink)
      // console.log('here')

      // Handle events
      socket.emit('createUserChannel', user?._id)
      socket.on('notifyUser', (data: any) => {
        dispatch(updateNotifications(data))
      })

      // Listen for blockchain validation events
      socket.on('blockchainValidation', (data: any) => {
        console.log('🔔 Received blockchain validation:', data)
        console.log('🔔 Metadata received:', data.blockchainMetadata)

        // Handle all post types (post, team_shout, game_shout)
        if (data.type === 'post' || data.type === 'team_shout' || data.type === 'game_shout') {
          console.log('🔔 Updating post:', data.postId)
          dispatch(updatePostBlockchainMetadata({
            postId: data.postId,
            blockchainMetadata: data.blockchainMetadata
          }))
        } else if (data.type === 'comment') {
          console.log('🔔 Updating comment:', data.commentId)
          dispatch(updateCommentBlockchainMetadata({
            commentId: data.commentId,
            newsId: data.newsId,
            blockchainMetadata: data.blockchainMetadata
          }))
        }
      })

      // Cleanup on unmount
      return () => {
        socket.off('notifyUser')
        socket.off('blockchainValidation')
        socket.disconnect()
      }
    }
  }, [user?._id, dispatch])

  // Investor pages manage their own auth states and standalone navigation.
  if ([ROUTES.Investors, ROUTES.InvestorAdmin].includes(location.pathname.toLowerCase().replace(/\/+$/, '') as ROUTES)) {
    return <>{children}</>
  }

  // if user is loading, show loading screen
  // If not logged in, show public pages without Layout wrapper
  // A Privy session that is definitely gone means logged out, even if a stale
  // isLoading flag is still set.
  const isLoggedOut = !user?._id && (!isLoading || (privyReady && !privyAuthenticated))
  if (isLoggedOut) {
    if (isPublicRoute) {
      // Render public routes without Layout wrapper
      return <>{children}</>
    }
    // Allow viewing shouts/posts and tournaments without authentication (for sharing)
    if (isShoutRoute || isTournamentRoute || isStatsRoute || isBracketShareRoute) {
      // Save tournament URL if user lands on tournament page while not logged in
      // This handles the case where user does hard refresh on tournament page
      if (isTournamentRoute && location.pathname !== '/') {
        const existingReturnUrl = localStorage.getItem('privy_return_url') || sessionStorage.getItem('privy_return_url')
        if (!existingReturnUrl || existingReturnUrl !== location.pathname) {
          console.log('💾 Saving tournament URL for non-logged-in user:', location.pathname)
          localStorage.setItem('privy_return_url', location.pathname)
          sessionStorage.setItem('privy_return_url', location.pathname)
          localStorage.setItem('login_from_tournament', 'true')
          sessionStorage.setItem('login_from_tournament', 'true')
        }
      }
      // Don't show full layout - will show below with limited sidebar
    } else {
      // Default to landing page for all other routes
      // Subdomain routing:
      // - cloud.cyberlete.net -> DePIN landing page
      // - gameservers.cyberlete.net -> Game Servers landing page
      // - cyberlete.net -> Main gaming landing page
      // Wrapped in Suspense for lazy loading
      const LandingFallback = (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#030504'
        }}>
          <div style={{
            color: '#02f305',
            fontSize: '1.2rem',
            fontWeight: 600
          }}>
            Loading...
          </div>
        </div>
      )

      if (isCloudSubdomain()) {
        return <Suspense fallback={LandingFallback}><DePIN /></Suspense>
      }
      if (isGameServersSubdomain()) {
        return <Suspense fallback={LandingFallback}><GameServer /></Suspense>
      }
      return <Suspense fallback={LandingFallback}><LandingPage /></Suspense>
    }
  }

  return (
    <div className="bg-black flex w-full h-full justify-center">
      <div className={`w-full ${fullWidth ? 'max-w-[1920px] pl-4 lg:pl-8' : 'max-w-[1300px]'} min-h-screen flex-1 flex flex-row relative z-0 items-stretch box-border mx-auto`}>
        <header
          role="banner"
          className="hidden lg:flex flex-col shrink-0 basis-auto relative items-end z-[3]"
        >
          <div className="w-16 lg:w-20 xl:w-72 flex flex-col">
            <div className="flex flex-col w-16 lg:w-20 xl:w-72 h-full py-4 px-1 lg:px-2 fixed">
              <div className="flex items-center space-x-2 pl-2">
                <img className="w-12 h-12" src={Logo} alt="logo" />
                <Typography
                  className="hidden xl:flex"
                  variant="h4"
                  transform="uppercase"
                >
                  Cyberlete
                </Typography>
              </div>
              <div className="mt-8 space-y-2">
                {sideMenu
                  .filter((item) =>
                    isSideMenuItemVisible(item, { isLoggedIn, user }),
                  )
                  .map((item) => {
                    const isExploreParent =
                      item.path === ROUTES.Explore && Array.isArray(item.children)
                    const showChildren =
                      isExploreParent && isExploreRoute(location.pathname)
                    const resolvedPath =
                      item.path === ROUTES.Profile && user && user?.username
                        ? getProfileDetailRoute(user?.username)
                        : item.path
                    return (
                      <div key={item.path}>
                        <div
                          className="relative flex items-center justify-center xl:justify-start p-3 lg:p-4 space-x-2 cursor-pointer hover:bg-dark-gray-light hover:bg-opacity-40 rounded-full min-h-[48px]"
                          onClick={() => handleNavigate(resolvedPath)}
                        >
                          <Icon name={item.iconName as IconName} size={24} />
                          <Typography
                            className="hidden xl:flex"
                            variant="subtitle1"
                            fontWeight="semibold"
                          >
                            {item.label}
                          </Typography>
                          {item.path === ROUTES.Notifications &&
                            unReadNotifications > 0 && (
                              <div className="bg-red-500 text-white font-bold bg-green p-3 text-xs rounded-full w-4 h-4 flex items-center justify-center absolute left-0 lg:-left-1 top-0 lg:-top-0">
                                {unReadNotifications}
                              </div>
                            )}
                        </div>
                        {showChildren && (
                          <div className="relative mt-1 mb-1 space-y-1 xl:pl-6 xl:ml-5">
                            {/* Vertical neon-green glow line — brightest at
                                center, soft white wisps fading to transparent
                                at top/bottom. */}
                            <span
                              aria-hidden
                              className="hidden xl:block absolute left-0 top-0 bottom-0 pointer-events-none"
                              style={{
                                width: '0.5px',
                                background:
                                  'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.22) 14%, rgba(2,243,5,0.95) 50%, rgba(255,255,255,0.22) 86%, rgba(255,255,255,0) 100%)',
                                boxShadow:
                                  '0 0 6px rgba(2,243,5,0.55), 0 0 14px rgba(2,243,5,0.28)',
                              }}
                            />
                            {(item.children as SideMenuItem[])
                              .filter((child) =>
                                isSideMenuItemVisible(child, {
                                  isLoggedIn,
                                  user,
                                }),
                              )
                              .map((child) => {
                                const isActiveChild =
                                  location.pathname === child.path ||
                                  location.pathname.startsWith(`${child.path}/`)
                                return (
                                  <div
                                    key={child.path}
                                    className={`relative flex items-center justify-center xl:justify-start py-2 px-3 lg:px-4 space-x-2 cursor-pointer rounded-full min-h-[36px] transition-colors ${
                                      isActiveChild
                                        ? 'bg-dark-gray-light bg-opacity-50'
                                        : 'hover:bg-dark-gray-light hover:bg-opacity-30'
                                    }`}
                                    onClick={() => handleNavigate(child.path)}
                                  >
                                    <Icon name={child.iconName as IconName} size={18} />
                                    <Typography
                                      className="hidden xl:flex text-sm"
                                      variant="subtitle1"
                                      fontWeight={isActiveChild ? 'semibold' : 'medium'}
                                      color={isActiveChild ? undefined : 'secondary'}
                                    >
                                      {child.label}
                                    </Typography>
                                  </div>
                                )
                              })}
                          </div>
                        )}
                      </div>
                    )
                  })}
                {/* {isLoggedIn && (
                  <Button
                    className="w-full xl:w-10/12 !mt-4"
                    size="lg"
                    stretch
                    isFancy
                    onClick={() => setIsPostModalOpen(true)}
                  >
                    <Icon className="xl:hidden" name="shout" />
                    <Typography className="hidden xl:flex" variant="subtitle1">
                      Shout
                    </Typography>
                  </Button>
                )} */}
                {account && (
                  <Button
                    className="w-full xl:w-10/12 !mt-4"
                    size="lg"
                    stretch
                    onClick={disconnect}
                    variant="warn"
                  >
                    <Icon className="xl:hidden" name="shout" />
                    <Typography className="hidden xl:flex" variant="subtitle1">
                      Disconnect({formatAddress(account)})
                    </Typography>
                  </Button>
                  // <Box className="flex-col">
                  //   <Typography variant="subtitle1">Your wallet:</Typography>
                  //   <Box className="flex items-center" justify="between">
                  //     <Typography
                  //       className="hidden xl:flex"
                  //       variant="subtitle1"
                  //     >
                  //       {formatAddress(account)}
                  //     </Typography>
                  //     <Icon
                  //       className="w-8 h-8 p-2 rounded-full hover:bg-gray"
                  //       color="pink"
                  //       name="logout"
                  //       onClick={disconnect}
                  //     />
                  //   </Box>
                  // </Box>
                )}
              </div>
              {user && (
                <div className="flex grow w-full flex-col-reverse">
                  <Box alignCenter justify="between">
                    <div className="flex items-center space-x-2">
                      <img
                        className="w-10 h-10 rounded-full object-cover"
                        src={user?.avatar?.url ?? DefaultAvatar}
                        onError={({ currentTarget }) => {
                          currentTarget.onerror = null
                          currentTarget.src = DefaultAvatar
                        }}
                        alt="avatar"
                      />
                      <div className="xl:flex flex-col hidden">
                        <Typography transform="capitalize">
                          {user?.firstname} {user?.lastname}
                        </Typography>
                        <Typography color="secondary">
                          @{user?.username}
                        </Typography>
                      </div>
                    </div>
                    <div className="hidden xl:flex items-center space-x-1">
                      <button
                        aria-label="Open settings"
                        className="p-0 bg-transparent border-none"
                        onClick={() => setIsSettingsOpen(true)}
                      >
                        <Icon
                          className="w-8 h-8 p-2 rounded-full hover:bg-gray cursor-pointer"
                          color="secondary"
                          name="cog"
                        />
                      </button>
                      <button
                        aria-label="Log out"
                        className="p-0 bg-transparent border-none"
                        onClick={handleLogout}
                      >
                        <Icon
                          className="w-8 h-8 p-2 rounded-full hover:bg-gray cursor-pointer"
                          color="pink"
                          name="logout"
                        />
                      </button>
                    </div>
                  </Box>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="flex grow flex-col basis-auto relative items-start z-[3] min-h-screen pb-16 lg:pb-0 w-full min-w-0 overflow-x-hidden">
          <div className={`w-full max-w-[100vw] ${fullWidth ? '' : 'lg:w-[990px] lg:max-w-[990px]'} shrink grow items-stretch box-border flex basis-auto flex-col relative overflow-hidden`}>
            {hasRightNav ? (
              <div className="w-full flex grow flex-row justify-between items-stretch overflow-hidden">
                <div className="w-full max-w-[100vw] lg:max-w-[600px] z-[1] lg:border-l lg:border-r border-solid border-light-border dark:border-dark-border overflow-hidden">
                  {children}
                </div>
                <div className="hidden lg:block">
                  <RightSideNav />
                </div>
              </div>
            ) : (
              <div className="w-full max-w-[100vw] overflow-hidden">
                {children}
              </div>
            )}
          </div>
        </main>

        <AuthModal />
        <PostModal />
        <ProfileEditModal />
        <CreateTeamModal />
        <CreateCommunityModal />
        <CreateGameModal />
        <CreateTournamentModal />
        <SetScoreModal />
        <SetMatchWinnerModal />
        <SettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
        <BottomNav onOpenSettings={() => setIsSettingsOpen(true)} />
      </div>
    </div>
  )
}